import { createMemoryHistory, createRouter, createWebHistory, type Router } from 'vue-router';
import ClusterPage from '../pages/landing/ClusterPage.vue';
import LandingPage from '../pages/landing/LandingPage.vue';
import OfferPage from '../pages/legal/OfferPage.vue';
import PaymentRulesPage from '../pages/legal/PaymentRulesPage.vue';
import PrivacyPage from '../pages/legal/PrivacyPage.vue';
import TemplatePage from '../pages/landing/TemplatePage.vue';
import TemplatesPage from '../pages/landing/TemplatesPage.vue';
import { CLUSTERS } from './clusters.js';
import { PAGES } from './seo.js';

/** Пути, за которые отвечает эта точка входа. Их же знает nginx и dev-сервер. */
export const SITE_PATHS: string[] = PAGES.map((page) => page.path);

/**
 * Маршруты публичного сайта: витрина и три правовых документа.
 *
 * Отдельный роутер, а не часть кабинетного, и без единого гарда. Гард
 * кабинета зовёт `auth.initialize()`, то есть ходит в `/api/auth/me` — на
 * странице, куда человек пришёл из поиска, этот запрос заведомо кончается 401 и
 * не нужен никому.
 *
 * Страницы подключены СТАТИЧЕСКИ, а не через `import()`. В кабинете ленивая
 * загрузка на месте: разделов там три десятка. Здесь их четыре, а ленивый
 * импорт стоил бы дорого — Vite складывает стили асинхронного куска в JS и
 * подставляет их после его загрузки. Для пререндера это значит страницу без
 * оформления до исполнения скрипта, то есть ровно то, от чего пререндер и
 * заводился.
 */
export function createSiteRouter(ssr = false): Router {
  return createRouter({
    // На сборке истории браузера нет: адрес задаётся вызовом `router.push`.
    history: ssr ? createMemoryHistory() : createWebHistory(),
    routes: [
      { path: '/', name: 'landing', component: LandingPage },
      { path: '/offer', name: 'offer', component: OfferPage },
      { path: '/payment-rules', name: 'payment-rules', component: PaymentRulesPage },
      { path: '/privacy', name: 'privacy', component: PrivacyPage },
      /*
       * Каталог шаблонов и страница на каждый шаблон.
       *
       * Компонент один, а страниц столько, сколько шаблонов: их состав меняет
       * суперадмин в админке, а не выкладка сайта, поэтому перечислить их здесь
       * нельзя — данные приходят из API и лежат в `public-data.ts`. Пререндер
       * собирает по странице на шаблон из тех же данных.
       */
      { path: '/templates', name: 'templates', component: TemplatesPage },
      {
        path: '/templates/:slug',
        name: 'template',
        component: TemplatePage,
        /*
         * Адрес с `.html` канонизируется здесь, а не в ловушке в конце списка:
         * до неё он не доходит — один сегмент после `/templates/` перехватывает
         * `:slug`. Пререндер кладёт файлы `templates/<слаг>.html`, nginx отдаёт
         * их и по короткому адресу, и по длинному; при гидратации слаг
         * оказывался `kp-uslugi.html`, в каталоге такого нет, и живая страница
         * заменялась на «Шаблон не найден».
         */
        beforeEnter: (to) =>
          to.path.endsWith('.html') ? { path: to.path.replace(/\.html$/, '') } : true,
      },
      /*
       * Посадочные страницы под поисковые кластеры. Компонент один, содержание
       * у каждой своё и приходит пропом — см. `clusters.ts`.
       */
      ...CLUSTERS.map((cluster) => ({
        path: `/${cluster.slug}`,
        /*
         * Префикс обязателен. Слаг одного из кластеров — `landing`, и без
         * префикса имя совпало бы с именем витрины: vue-router считает имена
         * уникальными и молча заменяет запись, то есть статический маршрут `/`
         * просто исчезал бы. Дальше корень проваливался в общий маршрут-ловушку
         * и уходил в бесконечный редирект сам на себя.
         */
        name: `cluster-${cluster.slug}`,
        component: ClusterPage,
        props: { cluster },
      })),
      {
        /*
         * Адрес файла вместо адреса страницы: `/offer.html` вместо `/offer`.
         *
         * Файлы на диске лежат именно так, и nginx отдаёт их по обоим адресам —
         * разметка приедет правильная. Но роутер такого пути не знает, и после
         * гидратации страница стёрлась бы начисто: сервер показал документ,
         * клиент не нашёл маршрута и отрисовал пустоту. Здесь адрес приводится
         * к каноничному — тому же, что стоит в `<link rel="canonical">`.
         *
         * Всё непонятное уводится на витрину, а не в 404: своей страницы «не
         * найдено» у сайта из четырёх адресов нет, и попасть сюда можно только
         * по опечатке в чужой ссылке.
         */
        path: '/:pathMatch(.*)*',
        redirect: (to) => {
          const path = to.path.replace(/\.html$/, '');
          /*
           * Каталог проверяется по префиксу, а не по списку: страниц там
           * столько, сколько шаблонов, и они приходят из API, а `SITE_PATHS`
           * собран из статического перечня. Несуществующий слаг сама страница
           * покажет как «шаблон не найден» — это честнее молчаливого увода на
           * витрину.
           */
          if (path === '/templates' || path.startsWith('/templates/')) return path;
          return SITE_PATHS.includes(path) ? path : '/';
        },
      },
    ],
    /*
     * Переход между документами начинается сверху, а не с середины прошлой
     * страницы. Якорь внутри лендинга при этом остаётся якорем: по нему и
     * ведут ссылки шапки.
     */
    scrollBehavior(to) {
      if (to.hash) return { el: to.hash, behavior: 'smooth' };
      return { top: 0 };
    },
  });
}
